import {
  ChevronDown,
  ChevronUp,
  History,
  ListMusic,
  Loader2,
  Maximize2,
  Mic2,
  Pause,
  Play,
  Repeat,
  Repeat1,
  Shuffle,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from 'react';

import KaraArtwork from '@/components/music/kara-artwork';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { playerController } from '@/lib/music/player-controller';
import {
  useMusicPlayerStore,
  type DrawerTab,
  type LoopMode,
} from '@/lib/music-player-store';

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function loopIcon(loop: LoopMode): LucideIcon {
  return loop === 'one' ? Repeat1 : Repeat;
}

/* ─── Atoms ──────────────────────────────────────────────────────────── */

/**
 * Wraps any trigger in a Radix tooltip showing `label` on hover/focus.
 * Player-bar icon buttons use this so users see what each one does
 * without guessing from the icon alone.
 */
function Hint({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" sideOffset={6}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function PlayPauseButton({
  size = 'md',
}: {
  size?: 'sm' | 'md' | 'lg';
}): ReactElement {
  const isPlaying = useMusicPlayerStore((s) => s.isPlaying);
  const isBuffering = useMusicPlayerStore((s) => s.isBuffering);
  const dim =
    size === 'lg' ? 'h-11 w-11' : size === 'md' ? 'h-10 w-10' : 'h-8 w-8';
  const icon =
    size === 'lg' ? 'h-5 w-5' : size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const label = isPlaying ? 'Pause' : 'Play';
  return (
    <Hint label={label}>
      <button
        type="button"
        onClick={() => playerController.toggle()}
        className={`inline-flex ${dim} shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-transform hover:scale-105`}
        aria-label={label}
      >
        {isBuffering ? (
          <Loader2 className={`${icon} animate-spin`} />
        ) : isPlaying ? (
          <Pause className={icon} />
        ) : (
          <Play className={`${icon} translate-x-[1px] fill-current`} />
        )}
      </button>
    </Hint>
  );
}

function SecondaryControl({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactElement;
}): ReactElement {
  return (
    <Hint label={ariaLabel}>
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={active}
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
          active
            ? 'border-primary/40 bg-primary/15 text-primary'
            : 'border-border/70 bg-background/40 text-muted-foreground hover:text-foreground'
        }`}
      >
        {children}
      </button>
    </Hint>
  );
}

/* ─── Track chip (shared between mobile + desktop) ───────────────────── */

function TrackChip({
  small,
}: {
  small?: boolean;
}): ReactElement | null {
  const track = useMusicPlayerStore((s) => s.currentTrack);
  if (!track) return null;
  const seq = track.sequence ? ` ${track.sequence}` : '';
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <KaraArtwork
        kind={track.kind}
        animeId={track.animeId}
        animeName={track.animeName}
        className={small ? 'h-9 w-9' : 'h-11 w-11'}
      />
      <div className="min-w-0 flex-1">
        <p
          className="line-clamp-1 text-sm font-semibold text-foreground"
          title={track.title}
        >
          {track.title}
        </p>
        <p
          className="line-clamp-1 text-[11px] text-muted-foreground"
          title={track.animeName}
        >
          <span className="font-semibold uppercase tracking-wider text-primary/85">
            {track.kind}
            {seq}
          </span>
          {track.animeName ? ` · ${track.animeName}` : ''}
        </p>
      </div>
    </div>
  );
}

/* ─── Transport + progress + side actions ────────────────────────────── */

function TransportControls(): ReactElement {
  const shuffle = useMusicPlayerStore((s) => s.shuffle);
  const loop = useMusicPlayerStore((s) => s.loop);
  const queueLength = useMusicPlayerStore((s) => s.queue.length);
  const toggleShuffle = useMusicPlayerStore((s) => s.toggleShuffle);
  const cycleLoop = useMusicPlayerStore((s) => s.cycleLoop);
  const LoopIcon = loopIcon(loop);
  const hasQueue = queueLength > 1;

  return (
    <div className="flex items-center justify-center gap-2">
      <SecondaryControl
        active={shuffle}
        onClick={toggleShuffle}
        ariaLabel="Toggle shuffle"
      >
        <Shuffle className="h-3.5 w-3.5" />
      </SecondaryControl>
      <Hint label="Previous track">
        <button
          type="button"
          onClick={() => playerController.previous()}
          disabled={!hasQueue}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/40 text-foreground transition-colors hover:bg-card disabled:opacity-40"
          aria-label="Previous track"
        >
          <SkipBack className="h-4 w-4" />
        </button>
      </Hint>
      <PlayPauseButton size="md" />
      <Hint label="Next track">
        <button
          type="button"
          onClick={() => playerController.next()}
          disabled={!hasQueue}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/40 text-foreground transition-colors hover:bg-card disabled:opacity-40"
          aria-label="Next track"
        >
          <SkipForward className="h-4 w-4" />
        </button>
      </Hint>
      <SecondaryControl
        active={loop !== 'off'}
        onClick={cycleLoop}
        ariaLabel={`Loop mode: ${loop}`}
      >
        <LoopIcon className="h-3.5 w-3.5" />
      </SecondaryControl>
    </div>
  );
}

function ProgressBar(): ReactElement {
  const currentTime = useMusicPlayerStore((s) => s.currentTime);
  const duration = useMusicPlayerStore((s) => s.duration);
  const safeDuration = duration > 0 ? duration : 0;
  return (
    <div className="flex w-full items-center gap-2">
      <span className="w-10 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
        {formatTime(currentTime)}
      </span>
      <input
        type="range"
        min={0}
        max={safeDuration || 0}
        step={0.5}
        value={Math.min(currentTime, safeDuration || currentTime)}
        onChange={(e) =>
          playerController.seek(Number.parseFloat(e.target.value))
        }
        disabled={safeDuration <= 0}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted/40 accent-primary disabled:cursor-not-allowed"
        aria-label="Seek"
      />
      <span className="w-10 shrink-0 text-[10px] tabular-nums text-muted-foreground">
        {formatTime(safeDuration)}
      </span>
    </div>
  );
}

function VolumeControl(): ReactElement {
  const volume = useMusicPlayerStore((s) => s.volume);
  const isMuted = useMusicPlayerStore((s) => s.isMuted);
  const [open, setOpen] = useState(false);
  return (
    <div
      className="flex items-center gap-2"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <SecondaryControl
        onClick={() => playerController.toggleMute()}
        ariaLabel={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted || volume === 0 ? (
          <VolumeX className="h-3.5 w-3.5" />
        ) : (
          <Volume2 className="h-3.5 w-3.5" />
        )}
      </SecondaryControl>
      <input
        type="range"
        min={0}
        max={1}
        step={0.02}
        value={isMuted ? 0 : volume}
        onChange={(e) =>
          playerController.setVolume(Number.parseFloat(e.target.value || '0'))
        }
        className={`h-1 cursor-pointer appearance-none rounded-full bg-muted/40 accent-primary transition-all ${
          open ? 'w-20 opacity-100' : 'pointer-events-none w-0 opacity-0'
        }`}
        aria-label="Volume"
      />
    </div>
  );
}

function DrawerToggleButton({
  icon: Icon,
  tab,
  ariaLabel,
}: {
  icon: LucideIcon;
  tab: DrawerTab;
  ariaLabel: string;
}): ReactElement {
  const drawerOpen = useMusicPlayerStore((s) => s.isDrawerOpen);
  const drawerTab = useMusicPlayerStore((s) => s.drawerTab);
  const openDrawer = useMusicPlayerStore((s) => s.openDrawer);
  const closeDrawer = useMusicPlayerStore((s) => s.closeDrawer);
  const active = drawerOpen && drawerTab === tab;
  return (
    <SecondaryControl
      active={active}
      onClick={() => {
        if (active) closeDrawer();
        else openDrawer(tab);
      }}
      ariaLabel={ariaLabel}
    >
      <Icon className="h-3.5 w-3.5" />
    </SecondaryControl>
  );
}

function SideActions(): ReactElement {
  const openImmersive = useMusicPlayerStore((s) => s.openImmersive);
  const closePlayer = useMusicPlayerStore((s) => s.closePlayer);
  // Karaoke-mode toggle lives in the immersive view only — toggling it
  // from the miniplayer is meaningless since the video can't be seen
  // from a compact bar.
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <DrawerToggleButton icon={Mic2} tab="lyrics" ariaLabel="Lyrics" />
      <DrawerToggleButton icon={ListMusic} tab="queue" ariaLabel="Queue" />
      <DrawerToggleButton
        icon={History}
        tab="history"
        ariaLabel="History"
      />
      <SecondaryControl
        onClick={openImmersive}
        ariaLabel="Immersive view"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </SecondaryControl>
      <VolumeControl />
      <SecondaryControl onClick={closePlayer} ariaLabel="Close player">
        <X className="h-3.5 w-3.5" />
      </SecondaryControl>
    </div>
  );
}

/* ─── Desktop layouts ────────────────────────────────────────────────── */

function DesktopCollapsed(): ReactElement {
  const togglePlayerExpanded = useMusicPlayerStore(
    (s) => s.togglePlayerExpanded
  );
  const closePlayer = useMusicPlayerStore((s) => s.closePlayer);
  return (
    <div className="hidden items-center gap-3 px-3 py-2.5 lg:flex">
      <TrackChip />
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <PlayPauseButton size="sm" />
        <SecondaryControl
          onClick={togglePlayerExpanded}
          ariaLabel="Expand player"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </SecondaryControl>
        <SecondaryControl onClick={closePlayer} ariaLabel="Close player">
          <X className="h-3.5 w-3.5" />
        </SecondaryControl>
      </div>
    </div>
  );
}

function DesktopExpanded(): ReactElement {
  const togglePlayerExpanded = useMusicPlayerStore(
    (s) => s.togglePlayerExpanded
  );
  return (
    <div className="hidden flex-col gap-2 px-4 py-3 lg:flex">
      <div className="flex items-center gap-3">
        <TrackChip />
        <div className="ml-auto shrink-0">
          <SecondaryControl
            onClick={togglePlayerExpanded}
            ariaLabel="Collapse player"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </SecondaryControl>
        </div>
      </div>
      <ProgressBar />
      <TransportControls />
      <SideActions />
    </div>
  );
}

/* ─── Mobile compact view ────────────────────────────────────────────── */

function MobileBar(): ReactElement {
  const queueLength = useMusicPlayerStore((s) => s.queue.length);
  const openImmersive = useMusicPlayerStore((s) => s.openImmersive);
  const openDrawer = useMusicPlayerStore((s) => s.openDrawer);
  const closePlayer = useMusicPlayerStore((s) => s.closePlayer);

  // Swipe-up gesture to expand into the immersive drawer. We fire
  // `openImmersive()` mid-drag (touchmove after ~24px of upward travel)
  // rather than on release, so the drawer animation begins while the
  // finger is still moving — it feels like the miniplayer itself is
  // expanding into immersive instead of being a discrete trigger.
  // 24px is small enough that the gesture chains naturally with the
  // user's intent but large enough that taps + tiny finger jitter
  // don't fire it.
  const touchStart = useRef<{ y: number } | null>(null);
  const fired = useRef(false);
  const handleTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    touchStart.current = { y: e.touches[0].clientY };
    fired.current = false;
  };
  const handleTouchMove = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!touchStart.current || fired.current) return;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (dy < -24) {
      fired.current = true;
      openImmersive();
    }
  };
  const handleTouchEnd = () => {
    touchStart.current = null;
    fired.current = false;
  };

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 touch-pan-y lg:hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button
        type="button"
        onClick={openImmersive}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <TrackChip />
      </button>
      <button
        type="button"
        onClick={() => openDrawer('queue')}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/40 text-muted-foreground"
        aria-label="Open queue"
      >
        <ListMusic className="h-3.5 w-3.5" />
        {queueLength > 0 ? (
          <span className="sr-only">{queueLength} in queue</span>
        ) : null}
      </button>
      <PlayPauseButton size="md" />
      <button
        type="button"
        onClick={openImmersive}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/40 text-muted-foreground"
        aria-label="Expand"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={closePlayer}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/40 text-muted-foreground"
        aria-label="Close player"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ─── Root ───────────────────────────────────────────────────────────── */

/**
 * Sticky bottom player bar.
 *
 * Mobile: full-width compact bar — tap to open immersive.
 * Desktop: bottom-right toast. Collapsed by default (artwork + title +
 * play/pause + chevron). Click the chevron to expand into the full
 * transport + progress + side actions stack.
 *
 * Hides while the immersive overlay is open — that view owns the
 * player surface and a sticky bar behind it would be redundant chrome.
 */
export function MusicPlayerBar(): ReactElement | null {
  const track = useMusicPlayerStore((s) => s.currentTrack);
  const isVisible = useMusicPlayerStore((s) => s.isVisible);
  const isImmersive = useMusicPlayerStore((s) => s.isImmersive);
  const isExpanded = useMusicPlayerStore((s) => s.isPlayerExpanded);
  if (!isVisible || !track || isImmersive) return null;

  // Desktop width animates between the two states; mobile stays edge-
  // anchored full-width so the compact bar fills the bottom rail.
  const desktopWidth = isExpanded ? 'lg:w-[520px]' : 'lg:w-[360px]';

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <div
        // Mobile: 16px clear of the 56px bottom navbar so the toast
        // doesn't sit flush against the nav. Desktop: anchored 4px
        // from the bottom corner.
        className={`fixed bottom-[72px] left-2 right-2 z-50 overflow-hidden rounded-2xl border border-border/70 bg-card/95 shadow-xl backdrop-blur-xl transition-all duration-300 ease-out md:bottom-4 lg:left-auto lg:right-4 ${desktopWidth}`}
        role="region"
        aria-label="Music player"
      >
        <MobileBar />
        {isExpanded ? <DesktopExpanded /> : <DesktopCollapsed />}
      </div>
    </TooltipProvider>
  );
}

export default MusicPlayerBar;
